if (!customElements.get('custom-bundle-selector')) {
  customElements.define(
    'custom-bundle-selector',
    class CustomBundleSelector extends HTMLElement {
      constructor() {
        super();
        
        this.maxProducts = parseInt(this.dataset.maxProducts) || 5;
        this.discountCode = this.dataset.discountCode || '';
        this.selectedItems = new Map();
        
        this.itemsContainer = this.querySelector('[data-bundle-items]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.totalElements = this.querySelectorAll('[data-bundle-total], [data-bundle-total-mobile]');
        this.counterElement = this.querySelector('.current-count');
        this.errorMessage = this.querySelector('.product-form__error-message');
        
        this.init();
      }
      
      init() {
        // Add event listeners to all add buttons
        document.querySelectorAll('.custom-bundle-add-btn').forEach(button => {
          button.addEventListener('click', this.handleAddProduct.bind(this));
        });
        
        // Add event listener to submit button
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
        }
      }
      
      handleAddProduct(event) {
        const button = event.currentTarget;
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId;
        
        // Check if product is already in bundle
        if (this.selectedItems.has(productId)) {
          this.removeProduct(productId);
          return;
        }
        
        // Check if we've reached the maximum
        if (this.selectedItems.size >= this.maxProducts) {
          this.showError(`Solo puedes seleccionar hasta ${this.maxProducts} productos`);
          return;
        }
        
        // Add product to bundle
        const productData = {
          id: productId,
          variantId: variantId,
          title: button.dataset.productTitle,
          price: parseInt(button.dataset.productPrice),
          image: button.dataset.productImage
        };
        
        this.addProduct(productData, button);
      }
      
      addProduct(productData, button) {
        // Add to selected items
        this.selectedItems.set(productData.id, productData);
        
        // Update button state
        button.classList.add('is-added');
        button.setAttribute('aria-pressed', 'true');
        
        // Create bundle item element
        const bundleItem = this.createBundleItem(productData);
        this.itemsContainer.appendChild(bundleItem);
        
        // Update UI
        this.updateCounter();
        this.updateTotal();
        this.updateSubmitButton();
        this.hideError();
      }
      
      removeProduct(productId) {
        // Remove from selected items
        this.selectedItems.delete(productId);
        
        // Update button state
        const button = document.querySelector(`.custom-bundle-add-btn[data-product-id="${productId}"]`);
        if (button) {
          button.classList.remove('is-added');
          button.removeAttribute('aria-pressed');
        }
        
        // Remove bundle item element
        const bundleItem = this.itemsContainer.querySelector(`[data-bundle-item-id="${productId}"]`);
        if (bundleItem) {
          bundleItem.remove();
        }
        
        // Update UI
        this.updateCounter();
        this.updateTotal();
        this.updateSubmitButton();
        this.hideError();
      }
      
      createBundleItem(productData) {
        const item = document.createElement('div');
        item.className = 'bundle-item';
        item.setAttribute('data-bundle-item-id', productData.id);
        
        const priceFormatted = this.formatMoney(productData.price);
        
        item.innerHTML = `
          <div class="bundle-item__media">
            <img src="${productData.image}" alt="${productData.title}" loading="lazy">
          </div>
          <div class="bundle-item__details">
            <div class="bundle-item__title">${productData.title}</div>
            <div class="bundle-item__price">${priceFormatted}</div>
          </div>
          <button type="button" class="bundle-item__remove" aria-label="Eliminar ${productData.title}">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        `;
        
        // Add remove event listener
        const removeButton = item.querySelector('.bundle-item__remove');
        removeButton.addEventListener('click', () => {
          this.removeProduct(productData.id);
        });
        
        return item;
      }
      
      updateCounter() {
        if (this.counterElement) {
          this.counterElement.textContent = this.selectedItems.size;
          
          const counterContainer = this.counterElement.closest('.custom-bundle-counter');
          if (counterContainer) {
            if (this.selectedItems.size >= this.maxProducts) {
              counterContainer.classList.add('is-full');
            } else {
              counterContainer.classList.remove('is-full');
            }
          }
        }
      }
      
      updateTotal() {
        let total = 0;
        this.selectedItems.forEach(item => {
          total += item.price;
        });
        
        const formattedTotal = this.formatMoney(total);
        this.totalElements.forEach(element => {
          element.textContent = formattedTotal;
        });
      }
      
      updateSubmitButton() {
        if (this.submitButton) {
          if (this.selectedItems.size > 0) {
            this.submitButton.removeAttribute('disabled');
          } else {
            this.submitButton.setAttribute('disabled', 'true');
          }
        }
      }
      
      async handleSubmit(event) {
        event.preventDefault();
        
        if (this.selectedItems.size === 0) {
          this.showError('Debes seleccionar al menos un producto');
          return;
        }
        
        // Prepare items for cart
        const items = Array.from(this.selectedItems.values()).map(item => ({
          id: item.variantId,
          quantity: 1
        }));
        
        // Set loading state
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.setAttribute('disabled', 'true');
        this.hideError();
        
        try {
          // Add items to cart
          const response = await fetch(theme.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || 'Error al agregar al carrito');
          }
          
          const data = await response.json();
          
          // Get updated cart
          const cartResponse = await fetch(theme.routes.cart_url, {
            headers: {
              'Accept': 'application/json'
            }
          });
          
          const cart = await cartResponse.json();
          
          // Dispatch cart update event
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-bundle-selector',
            cart: cart
          });
          
          // Show success state
          this.submitButton.classList.add('success');
          
          // Open cart drawer if available
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            cartDrawer.show(this.submitButton);
          }
          
          // Reset bundle after a delay
          setTimeout(() => {
            this.resetBundle();
          }, 1000);
          
        } catch (error) {
          console.error('Error adding to cart:', error);
          this.showError(error.message || 'Error al agregar al carrito');
        } finally {
          this.submitButton.removeAttribute('aria-busy');
          this.submitButton.classList.remove('success');
        }
      }
      
      resetBundle() {
        // Clear all selected items
        const productIds = Array.from(this.selectedItems.keys());
        productIds.forEach(id => {
          this.removeProduct(id);
        });
      }
      
      showError(message) {
        if (this.errorMessage) {
          this.errorMessage.textContent = message;
          this.errorMessage.removeAttribute('hidden');
          
          // Auto-hide after 5 seconds
          setTimeout(() => {
            this.hideError();
          }, 5000);
        }
      }
      
      hideError() {
        if (this.errorMessage) {
          this.errorMessage.setAttribute('hidden', 'true');
          this.errorMessage.textContent = '';
        }
      }
      
      formatMoney(cents) {
        // Use Shopify's money format if available
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
        }
        
        // Fallback formatting
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
      }
    }
  );
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Ensure all custom bundle selectors are initialized
  document.querySelectorAll('custom-bundle-selector').forEach(element => {
    if (!element.initialized) {
      element.initialized = true;
    }
  });
});
